import {
  Component,
  computed,
  effect,
  inject,
  input,
  OnInit,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import {CommonModule} from '@angular/common';
import {ButtonModule} from 'primeng/button';
import {DialogModule} from 'primeng/dialog';
import {InputTextModule} from 'primeng/inputtext';
import {MultiSelectModule} from 'primeng/multiselect';
import {
  FormBuilder,
  FormGroup,
  FormGroupDirective,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {TranslocoDirective, TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {forkJoin} from 'rxjs';
import {DividerModule} from 'primeng/divider';
import {TreeSelectModule} from 'primeng/treeselect';
import {TreeNode} from 'primeng/api';
import {
  CreateUpdateProjectUserRequest,
  ProjectRoleType,
  ProjectStepType,
  ProjectUserDetailResponse,
} from '@frontend/shared-api';
import {PageMapping} from '../../../../../shared/model/page-tree.types';
import {FormFieldComponent} from '../../../../../shared/components/inputs/form-field/form-field.component';
import {MessageModule} from 'primeng/message';
import {
  EnumTranslationMapperService,
  TranslatedOption,
} from '../../../../../shared/services/enum-translation-mapper.service';
import {PageTreeService} from '../../../../../shared/services/page-tree.service';
import {Select} from 'primeng/select';
import {DirectusTooltipComponent} from '../../../../../shared/components/directus-tooltip/directus-tooltip.component';
import {FlattenedProjectStep} from '@frontend/directus';

interface ExtendedPageMapping extends PageMapping {
  originalChapterIndex: number;
  originalStepIndices: number[];
}

@Component({
  selector: 'app-add-edit-user-dialog',
  imports: [
    CommonModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    MultiSelectModule,
    TranslocoDirective,
    TranslocoPipe,
    DividerModule,
    TreeSelectModule,
    FormFieldComponent,
    MessageModule,
    ReactiveFormsModule,
    Select,
    DirectusTooltipComponent,
  ],
  templateUrl: './add-edit-user-dialog.component.html',
})
export class AddEditUserDialogComponent implements OnInit {
  display = input(false);
  displayChange = output<boolean>();
  userToEdit = input<ProjectUserDetailResponse | null>(null);
  isEditMode = input(false);
  preselectedRoles = input<ProjectRoleType[]>([]);
  preselectedAccessiblePages = input<ProjectStepType[]>([]);
  readonly save = output<CreateUpdateProjectUserRequest>();
  readonly formDirective = viewChild(FormGroupDirective);

  visible = signal(false);
  loading = signal(false);

  roleOptions = signal<TranslatedOption<ProjectRoleType>[]>([]);
  pageOptions = signal<TreeNode[]>([]);
  selectedRole = signal<ProjectRoleType | null>(null);

  isContributorSelected = computed(() => this.selectedRole() === ProjectRoleType.CONTRIBUTOR);

  userForm!: FormGroup;
  private readonly formBuilder = inject(FormBuilder);
  private readonly enumTranslationService = inject(EnumTranslationMapperService);
  private readonly pageTreeService = inject(PageTreeService);
  private readonly transloco = inject(TranslocoService);

  constructor() {
    effect(() => this.syncVisibilityAndReset());
    effect(() => this.syncUserPrefill());
    effect(() => this.applyPreselectedPagesWhenReady());
  }

  private syncVisibilityAndReset() {
    this.visible.set(this.display());
    if (this.visible() && this.userForm && !this.isEditMode()) {
      untracked(() => this.resetForm());
    }
  }

  private syncUserPrefill() {
    const user = this.userToEdit();
    if (user && this.isEditMode() && this.userForm && !this.loading()) {
      untracked(() => this.prefillForm(user));
    }
  }

  private applyPreselectedPagesWhenReady() {
    const preselectedPages = this.preselectedAccessiblePages();
    const pageOptions = this.pageOptions();
    const visible = this.visible();
    const isEditMode = this.isEditMode();

    if (
      visible &&
      !isEditMode &&
      preselectedPages.length > 0 &&
      pageOptions.length > 0 &&
      this.userForm
    ) {
      untracked(() => {
        const preselectedPagesAsStrings = preselectedPages.map((page) => page as string);
        const selectedNodes = this.getSelectedNodesFromPages(preselectedPagesAsStrings);
        if (selectedNodes.length > 0) {
          this.userForm.patchValue({selectedNodes});
        }
      });
    }
  }

  ngOnInit() {
    this.initForm();

    const control = this.userForm.controls['role'];
    this.selectedRole.set(control.value || null);
    control.valueChanges.subscribe((val) => this.selectedRole.set(val || null));

    this.loadOptions();
    this.visible.set(this.display());
  }

  private initForm() {
    this.userForm = this.formBuilder.group({
      firstName: [{value: '', disabled: this.isEditMode()}, Validators.required],
      lastName: [{value: '', disabled: this.isEditMode()}, Validators.required],
      email: [{value: '', disabled: this.isEditMode()}, [Validators.required, Validators.email]],
      role: [null, Validators.required],
      selectedNodes: [[]],
    });

    this.userForm.get('role')?.valueChanges.subscribe((role: ProjectRoleType) => {
      const pagesControl = this.userForm.controls['selectedNodes'];
      if (role === ProjectRoleType.CONTRIBUTOR) {
        pagesControl.setValidators(Validators.required);
      } else {
        pagesControl.clearValidators();
        pagesControl.setValue([]);
      }
      pagesControl.updateValueAndValidity();
    });
  }

  private prefillForm(user: ProjectUserDetailResponse) {
    try {
      let userRolesArray: ProjectRoleType[] = [];
      if (Array.isArray(user.roles)) {
        userRolesArray = user.roles;
      } else if (user.roles) {
        userRolesArray = Array.from(user.roles);
      }

      const selectedRole = userRolesArray.length > 0 ? userRolesArray[0] : null;

      let accessiblePages: string[] = [];
      if (Array.isArray(user.accessibleSteps)) {
        accessiblePages = user.accessibleSteps;
      } else if (user.accessibleSteps) {
        accessiblePages = Array.from(user.accessibleSteps);
      }

      const selectedNodes = this.getSelectedNodesFromPages(accessiblePages);

      this.userForm.patchValue({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        role: selectedRole,
        selectedNodes: selectedNodes,
      });

      this.selectedRole.set(selectedRole);
    } catch (err) {
      console.error('Error in prefillForm:', err);
    }
  }

  private getSelectedNodesFromPages(accessiblePages: string[]): TreeNode[] {
    let selectedNodes: TreeNode[] = [];
    try {
      selectedNodes = this.pageTreeService.getSelectedNodesFromPages(
        accessiblePages,
        this.pageOptions(),
      );
    } catch (err) {
      console.error('Error finding tree nodes for pages', err);
    }
    return selectedNodes;
  }

  private resetForm(): void {
    const preselectedRoles = this.preselectedRoles();
    const selectedRole = preselectedRoles.length > 0 ? preselectedRoles[0] : null;

    this.userForm.reset({
      firstName: '',
      lastName: '',
      email: '',
      role: selectedRole,
      selectedNodes: [],
    });
    this.selectedRole.set(selectedRole);
    const formDirective = this.formDirective();
    if (formDirective) {
      formDirective.resetForm();
    }
  }

  onCancel() {
    this.visible.set(false);
    this.displayChange.emit(false);
  }

  onAdd() {
    if (this.userForm.invalid) {
      Object.values(this.userForm.controls).forEach((control) => control.markAsTouched());
      return;
    }

    const userFormValue = this.userForm.value;
    const leafs = this.pageTreeService.getLeafNodeKeys(userFormValue.selectedNodes || []);
    const accessibleSteps = this.pageTreeService.getAccessibleStepsFromLeafKeys(leafs);

    const roles = [userFormValue.role as ProjectRoleType];

    const request: CreateUpdateProjectUserRequest = {
      firstName: this.isEditMode()
        ? this.userForm.getRawValue().firstName
        : userFormValue.firstName,
      lastName: this.isEditMode() ? this.userForm.getRawValue().lastName : userFormValue.lastName,
      email: this.isEditMode() ? this.userForm.getRawValue().email : userFormValue.email,
      roles,
      accessibleSteps,
    };

    this.save.emit(request);
    this.visible.set(false);
    this.displayChange.emit(false);
  }

  private loadOptions() {
    this.enumTranslationService
      .getAllRoleOptions()
      .subscribe((options: TranslatedOption<ProjectRoleType>[]) => {
        this.roleOptions.set(options);
      });

    forkJoin({
      accessibleStepOptions: this.enumTranslationService.getAccessibleStepOptions(),
      pageMappings: this.pageTreeService.loadPageMappings(),
      projectSteps: this.pageTreeService.loadProjectStepsFromDirectus(),
    }).subscribe(({accessibleStepOptions, pageMappings, projectSteps}) => {
      const accessibleStepEnums = accessibleStepOptions.map((option) => option.enum);

      const filteredPageMappings: ExtendedPageMapping[] = pageMappings
        .map((mapping, originalIndex) => {
          const filteredSubpagesWithIndices: Array<{step: ProjectStepType; originalIndex: number}> =
            [];
          mapping.subpages.forEach((subpage, stepIndex) => {
            if (accessibleStepEnums.includes(subpage)) {
              filteredSubpagesWithIndices.push({step: subpage, originalIndex: stepIndex});
            }
          });

          return {
            ...mapping,
            subpages: filteredSubpagesWithIndices.map((item) => item.step),
            originalStepIndices: filteredSubpagesWithIndices.map((item) => item.originalIndex),
            originalChapterIndex: originalIndex,
          };
        })
        .filter((mapping) => mapping.subpages.length > 0);

      const filteredProjectSteps = projectSteps.filter((step) => {
        if (!step.parent_step) {
          return projectSteps.some((childStep) => childStep.parent_step === step.id);
        }
        return true;
      });

      const treeNodes = this.buildTreeNodesWithCorrectNumbering(
        accessibleStepOptions,
        filteredPageMappings,
        filteredProjectSteps,
      );
      this.pageOptions.set(treeNodes);
    });
  }

  private buildTreeNodesWithCorrectNumbering(
    accessibleStepOptions: TranslatedOption<ProjectStepType>[],
    filteredPageMappings: ExtendedPageMapping[],
    filteredProjectSteps: FlattenedProjectStep[],
  ): TreeNode[] {
    const treeNodes: TreeNode[] = [];
    const stepTranslationMap = new Map(
      accessibleStepOptions.map((opt) => [opt.enum, opt.translation]),
    );
    const currentLang = this.transloco.getActiveLang();

    const chapterSteps = filteredProjectSteps.filter((step) => !step.parent_step);
    const childSteps = filteredProjectSteps.filter((step) => !!step.parent_step);

    const enumMap: Record<string, ProjectStepType> = {};

    const sortedChapterSteps = [...chapterSteps].sort(
      (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0),
    );

    const chapterKeyToDirectusMap = new Map<string, FlattenedProjectStep>();
    filteredPageMappings.forEach((mapping) => {
      const chapterIndex = mapping.originalChapterIndex;
      if (chapterIndex < sortedChapterSteps.length) {
        chapterKeyToDirectusMap.set(mapping.key, sortedChapterSteps[chapterIndex]);
      }
    });

    filteredPageMappings.forEach((mapping) => {
      const originalChapterNumber = mapping.originalChapterIndex + 1;
      const chapter = chapterKeyToDirectusMap.get(mapping.key);

      const childTreeNodes: TreeNode[] = [];
      const chapterChildSteps = chapter
        ? childSteps.filter((step) => step.parent_step === chapter.id)
        : [];

      mapping.subpages.forEach((stepType, filteredIndex) => {
        const originalStepIndex = mapping.originalStepIndices[filteredIndex];
        const originalStepNumber = originalStepIndex + 1;
        const key = `${mapping.key}_${originalStepNumber}`;
        enumMap[key] = stepType;

        const step = chapterChildSteps[originalStepIndex];
        const fallbackLabel =
          stepTranslationMap.get(stepType) ?? this.formatLabel(stepType.toString());
        const label = this.getStepTitle(step, currentLang, fallbackLabel);

        childTreeNodes.push({
          key,
          label: `${originalChapterNumber}.${originalStepNumber}. ${label}`,
          selectable: true,
        });
      });

      if (childTreeNodes.length > 0) {
        const chapterTitle = this.getStepTitle(chapter, currentLang, mapping.label);
        treeNodes.push({
          key: mapping.key,
          label: `${originalChapterNumber}. ${chapterTitle}`,
          children: childTreeNodes,
        });
      }
    });

    this.pageTreeService.setPageKeyToEnumMap(enumMap);

    return treeNodes;
  }

  private getStepTitle(
    step: FlattenedProjectStep | undefined,
    lang: string,
    fallback: string,
  ): string {
    if (!step) return fallback;
    return step.translations?.find((t) => t.language === lang)?.name ?? step.title ?? fallback;
  }

  private formatLabel(label: string): string {
    return label
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (l) => l.toUpperCase());
  }

  getInvalidClass(controlName: string): {'p-invalid': boolean} {
    const ctrl = this.userForm.get(controlName);
    return {
      'p-invalid': !!(ctrl && ctrl.invalid && (ctrl.touched || ctrl.dirty)),
    };
  }
}
